import {
  computed,
  createLogger,
  reactive,
  ref,
  shallowReactive,
} from "#imports";
import { uniq } from "lodash-es";
import type { UnwrapRef } from "vue";

interface RowWithId {
  id: string;
}

export const storeFactory = <T extends RowWithId, R = T>(
  name: string,
  queryIds: (ids: string[]) => Promise<{ data: T[] }>,
  mapper: (v: T) => R = (v) => v as unknown as R,
) => {
  const logger = createLogger(`factory/${name}`);
  const dataById = shallowReactive<Record<string, T>>({});
  const noData = ref<Array<string>>([]);

  const cacheById = (records: T[]) => {
    const recordsById = records.reduce(
      (acc, record) => ({ ...acc, [record.id]: record }),
      {} as Record<string, T>,
    );
    Object.assign(dataById, recordsById);
    return Object.keys(recordsById);
  };

  const getById = (id: string | null | undefined) =>
    computed(() => (id && dataById[id] ? mapper(dataById[id]) : undefined));

  const getByIds = (ids: string[]) =>
    computed(() => {
      const ret: R[] = [];
      for (const id of ids) {
        if (dataById[id]) {
          ret.push(mapper(dataById[id]));
        }
      }
      return ret;
    });

  const idsToLoad: string[] = [];
  const promiseResolvers: Array<() => void> = [];
  let loadTimeout: ReturnType<typeof setTimeout>;

  const deferLoading = async () => {
    clearTimeout(loadTimeout!);
    loadTimeout = setTimeout(doFetch, 0);

    const promise = new Promise<void>((res) => promiseResolvers.push(res));
    await promise;
  };

  const fetchByIds = async (ids: string[], forceRefetch: boolean = false) => {
    for (const id of ids) {
      if (
        forceRefetch ||
        (typeof dataById[id] === "undefined" && !noData.value.includes(id))
      ) {
        idsToLoad.push(id);
      }
    }

    if (idsToLoad.length > 0) {
      await deferLoading();
    }

    return getByIds(ids);
  };

  const fetchById = async (id: string, forceRefetch: boolean = false) => {
    if (
      forceRefetch ||
      (typeof dataById[id] === "undefined" && !noData.value.includes(id))
    ) {
      idsToLoad.push(id);
      await deferLoading();
    }

    return getById(id);
  };

  const doFetch = async () => {
    // copy what's loading to local scope and clear the global variable
    // it may be populated while we're loading
    let ids = uniq([...idsToLoad]);
    const resolvers = [...promiseResolvers];
    idsToLoad.length = 0;
    promiseResolvers.length = 0;

    while (ids.length > 0) {
      const loadingIds = ids.slice(0, 100); // fetch 100 at a time
      ids = ids.slice(100);

      const stopTimer = logger.time(`fetchByIds (${loadingIds.length} ids)`);
      const { data } = await queryIds(loadingIds);
      cacheById(data);

      if (data.length !== loadingIds.length) {
        // something didn't load, finds ids not in the cache
        const noDataIds = loadingIds.filter((id) => !dataById[id]);
        noData.value = uniq(noData.value.concat(noDataIds));
      }

      stopTimer();
    }
    for (const resolve of resolvers) {
      resolve();
    }
  };

  const fetchFactory = <TPayload>(
    query: (
      payload: UnwrapRef<TPayload>,
    ) => Promise<{ data: T[]; count?: number | null }>,
  ) => {
    const metadata = reactive<{
      req: TPayload | null;
      reqIds: Record<string, string[]>;
      reqTotalRows: Record<string, number>;
    }>({ req: null, reqIds: {}, reqTotalRows: {} });

    const ids = computed(() => metadata.reqIds[JSON.stringify(metadata.req)]);

    const result = computed(() =>
      ids.value ? getByIds(ids.value).value : null,
    );

    const totalRows = computed(
      () => metadata.reqTotalRows[JSON.stringify(metadata.req)] ?? 0,
    );

    const fetch = async (
      payload: UnwrapRef<TPayload>,
      forceRefetch: boolean = false,
    ) => {
      metadata.req = payload;
      const stringifiedPayload = JSON.stringify(payload);

      if (
        forceRefetch ||
        typeof metadata.reqIds[stringifiedPayload] === "undefined"
      ) {
        const stopTimer = logger.time(`fetch ${stringifiedPayload}`);
        const { data, count } = await query(payload);
        metadata.reqIds[stringifiedPayload] = cacheById(data);
        metadata.reqTotalRows[stringifiedPayload] = count ?? 0;
        stopTimer();
      }

      return getByIds(metadata.reqIds[stringifiedPayload]!);
    };

    return {
      metadata,
      result,
      totalRows,
      fetch,
    };
  };

  const fetchOneFactory = <TPayload>(
    query: (payload: UnwrapRef<TPayload>) => Promise<{ data: T | null }>,
  ) => {
    const metadata = reactive<{
      req: TPayload | null;
      reqIds: Record<string, string>;
    }>({ req: null, reqIds: {} });
    const id = computed(() => metadata.reqIds[JSON.stringify(metadata.req)]);
    const result = computed(() => getById(id.value).value);

    const fetch = async (
      payload: UnwrapRef<TPayload>,
      forceRefetch: boolean = false,
    ) => {
      metadata.req = payload;
      const stringifiedPayload = JSON.stringify(payload);
      if (
        forceRefetch ||
        typeof metadata.reqIds[stringifiedPayload] === "undefined"
      ) {
        const stopTimer = logger.time(`fetchOne ${stringifiedPayload}`);
        const { data } = await query(payload);
        // https://github.com/supabase/supabase-js/issues/1642
        metadata.reqIds[stringifiedPayload] = cacheById([data!])[0]!;
        stopTimer();
      }
      return getById(metadata.reqIds[stringifiedPayload]!);
    };

    return {
      metadata,
      result,
      fetch,
    };
  };

  const fetchOneByKeyFactory = <Key extends string & keyof T>(
    key: Key,
    query: (payload: string) => Promise<{ data: T | null }>,
  ) => {
    const metadata = reactive<{
      req: string | null;
    }>({ req: null });

    const keyToId = computed(() =>
      Object.entries(dataById).reduce(
        (acc, [id, data]) => ({ ...acc, [data[key] as string]: id }),
        {} as Record<string, string>,
      ),
    );

    const id = computed(() => keyToId.value[metadata?.req ?? ""]);
    const result = computed(() => getById(id.value).value);

    const fetch = async (
      payload: UnwrapRef<string>,
      forceRefetch: boolean = false,
    ) => {
      metadata.req = payload;
      if (forceRefetch || typeof keyToId.value[payload] === "undefined") {
        const { data } = await query(payload);
        // https://github.com/supabase/supabase-js/issues/1642
        cacheById([data!]);
      }
      return getById(keyToId.value[payload]);
    };

    return {
      metadata,
      result,
      fetch,
      keyToId,
    };
  };

  const fetchManyByKeysFactory = <Key extends string & keyof T>(
    key: Key,
    query: (payload: T[Key][]) => Promise<{ data: T[] }>,
  ) => {
    const metadata = reactive<{
      req: T[Key][] | null;
      reqIds: Record<string, string[]>;
    }>({ req: null, reqIds: {} });

    const result = (payloadKeys: NonNullable<T[Key]>[]) =>
      computed(() => {
        let ids: string[] = [];
        for (const payloadKey of payloadKeys) {
          const keyString = (payloadKey as string | number).toString();
          ids = ids.concat(metadata.reqIds[keyString] ?? []);
        }
        return getByIds(ids).value;
      });

    const fetch = async (
      payload: UnwrapRef<T[Key][]>,
      forceRefetch: boolean = false,
    ) => {
      metadata.req = payload;
      const toFetch: T[Key][] = [];
      for (const key of payload) {
        const keyString = (key as string | number).toString();
        if (forceRefetch || typeof metadata.reqIds[keyString] === "undefined") {
          toFetch.push(key as T[Key]);
        }
      }
      if (toFetch.length === 0) return;

      const stopTimer = logger.time(
        `fetchManyByKeys (${key}.length: ${toFetch.length})`,
      );
      const { data } = await query(toFetch);
      cacheById(data);

      for (const payloadKey of toFetch) {
        const keyString = (payloadKey as string | number).toString();
        metadata.reqIds[keyString] = data
          .filter((row) => row[key] === payloadKey)
          .map((row) => row.id);
      }

      stopTimer(`found: ${data.length}`);
    };

    return {
      metadata,
      result,
      fetch,
    };
  };

  return {
    dataById,
    fetchByIds,
    fetchById,
    getByIds,
    getById,
    fetchFactory,
    fetchOneFactory,
    fetchOneByKeyFactory,
    fetchManyByKeysFactory,
  };
};
